<template>
    <RedesignBaseSideModal
        :visible="visible"
        class="redesign-columns-manage-modal"
        data-testid="data-table-columns-manage-modal"
        @close="onCancel"
    >
        <template #header>
            {{ t('datatable.manage.columns') }}
        </template>

        <RedesignColumnsManage
            ref="columnsManageRef"
            :visible-columns="visibleColumns"
            :hidden-columns="hiddenColumns"
            :columns-config="columnsConfig"
            :global-headers-config="globalHeadersConfig"
            @change-label="emit('change-label', $event)"
            @change-icons-visibility="emit('change-icons-visibility', $event)"
            @update-columns-config="emit('update-columns-config', $event)"
            @regenerate-columns-config="emit('regenerate-columns-config')"
        />

        <template #footer>
            <VcButton
                type="outline"
                color="secondary"
                data-testid="data-table-columns-manage-cancel-button"
                @click="onCancel"
            >
                {{ t('common.cancel') }}
            </VcButton>
            <VcButton
                :icon="'Check'"
                data-testid="data-table-columns-manage-apply-button"
                @click="onApply"
            >
                {{ t('common.apply') }}
            </VcButton>
        </template>
    </RedesignBaseSideModal>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useConfirmBeforeClose } from '@/composables/useConfirmBeforeClose'
import RedesignBaseSideModal from '@/components/RedesignComponents/Modal/RedesignBaseSideModal.vue'
import RedesignColumnsManage from '@/components/RedesignComponents/RedesignTableComponents/RedesignColumnsManage.vue'
import { VcButton } from '@voicenter-team/voicenter-ui-plus'
import { ColumnsObjectConfig, TableHeadersConfig, WidgetTemplateColumnWithConfig } from '@/types/dashboard'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = withDefaults(
    defineProps<{
        visible: boolean
        hasUnsavedChanges?: boolean
        visibleColumns: WidgetTemplateColumnWithConfig[]
        hiddenColumns: WidgetTemplateColumnWithConfig[]
        columnsConfig: ColumnsObjectConfig
        globalHeadersConfig: TableHeadersConfig
    }>(),
    {
        hasUnsavedChanges: false,
        visibleColumns: () => [],
        hiddenColumns: () => [],
        columnsConfig: () => ({}),
    }
)

const emit = defineEmits<{
    (event: 'cancel-manage'): void
    (event: 'apply-changes'): void
    (event: 'change-label', payload: string): void
    (event: 'change-icons-visibility', payload: boolean): void
    (event: 'update-columns-config', payload: ColumnsObjectConfig): void
    (event: 'regenerate-columns-config'): void
}>()

const columnsManageRef = ref<InstanceType<typeof RedesignColumnsManage> | null>(null)
const { closeWithConfirm } = useConfirmBeforeClose()

function onApply () {
    columnsManageRef.value?.saveEditingColumnLabel()
    emit('apply-changes')
}

function onCancel () {
    if (props.hasUnsavedChanges) {
        closeWithConfirm(() => emit('cancel-manage'))
        return
    }
    emit('cancel-manage')
}
</script>

<style lang="scss">
.redesign-columns-manage-modal {
    .side-base-modal__body {
        padding: 0;
    }
}
</style>
